import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { LoqateEmailValidateDirective } from '@df/module-loqate/email/loqate-email.validator';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { Login } from '@df/session/login/login.interfaces';
import { NeedLoginService } from '@df/session/need-login.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ActionComponent } from '../../../ui/api/action.component';
import { ResultComponent } from '../../../ui/api/result.component';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../../ui/form/mui-input.directive';
import { ViewPasswordComponent } from '../../../ui/form/view-password.component';
import { CheckoutFormComponent } from '../../checkout-form.component';
import { CheckoutStepComponent } from '../../checkout-step.component';
import { CheckoutNewsletterComponent } from '../../newsletter/checkout-newsletter.component';
import { CheckoutAccountAbstract } from '../checkout-account.abstract';
import { CheckoutAccountService } from '../checkout-account.service';
import { CheckoutAccountNewCmsComponent } from './cms/checkout-account-new-cms.component';
import { NCheckoutAccountNewCms } from './cms/checkout-account-new-cms.interface';

export enum CheckoutAccountState {
    initial = 'initial',
    guestForm = 'guest_form',
    loginForm = 'login_form',
    createAccountForm = 'create_account_form'
}

@Component({
    selector: 'checkout-account-new',
    templateUrl: './checkout-account-new.component.html',
    styleUrl: './checkout-account-new.component.scss',
    standalone: true,
    imports: [
        ActionComponent,
        CheckboxComponent,
        CheckoutNewsletterComponent,
        CommonModule,
        FormsModule,
        ICmsClientModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        LoqateEmailValidateDirective,
        MuiInputDirective,
        NgTemplateOutlet,
        ResultComponent,
        TranslatePipe,
        ViewPasswordComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutAccountNewComponent extends CheckoutAccountAbstract implements OnInit {
    readonly content = input.required<NCheckoutAccountNewCms.IData>();

    // State management using signals
    protected readonly currentState = signal<CheckoutAccountState>(CheckoutAccountState.initial);

    private readonly parentComponent = inject(CheckoutAccountNewCmsComponent);
    private readonly checkoutAccountService = inject(CheckoutAccountService);

    // Computed properties for UI state
    protected readonly isInitialState = computed(() => this.currentState() === CheckoutAccountState.initial);
    protected readonly isGuestFormVisible = computed(() => this.currentState() === CheckoutAccountState.guestForm);
    protected readonly isLoginFormVisible = computed(() => this.currentState() === CheckoutAccountState.loginForm);
    protected readonly isCreateAccountFormVisible = computed(() => this.currentState() === CheckoutAccountState.createAccountForm);
    protected readonly showAccountExistsError = computed(() => this.checkoutAccountService.showAccountExistsError());

    protected termsAndConditionChecked?: boolean;

    constructor(
        checkoutFormComponent: CheckoutFormComponent,
        checkoutStepComponent: CheckoutStepComponent,
        requestService: RequestService,
        customer: Customer,
        app: App,
        ref: ChangeDetectorRef,
        needLoginService: NeedLoginService,
        timeoutService: TimeoutService
    ) {
        super(checkoutFormComponent, checkoutStepComponent, requestService, customer, app, ref, needLoginService, timeoutService);

        // Reset error states when state changes
        effect(() => {
            this.currentState(); // Track the signal
            this.checkoutAccountService.showAccountExistsError.set(false);
            this.checkoutAccountService.showAccountCreationSuccess.set(false);
        });
    }

    override ngOnInit() {
        super.ngOnInit();

        // Initialize state based on user status
        if (this.isLoggedIn) {
            this.currentState.set(CheckoutAccountState.initial);
        }

        // Subscribe to checkout events without email checking
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.LogoutSuccess).subscribe(() => {
                this.onLogoutSuccess();
                this.logoutSuccess = true;
                this.detectChanges();
            })
        );

        this.subscriptions.add(this.parentComponent.detectChanges$.subscribe(() => this.detectChanges()));
    }

    override get isValid(): boolean {
        if (this.customer.model.isLoggedIn) {
            return true;
        }

        switch (this.currentState()) {
            case CheckoutAccountState.initial:
                return false;
            default:
                return !!this.form?.valid || false;
        }
    }

    protected onBlockClick(block: NCheckoutAccountNewCms.IBlock): void {
        if (block.type === 'guest_checkout') {
            this.showGuestForm();
        } else if (block.type === 'sign_in') {
            this.showLoginForm();
        } else if (block.type === 'create_account') {
            this.showCreateAccountForm();
        }
    }

    // State transition methods
    showGuestForm(): void {
        this.currentState.set(CheckoutAccountState.guestForm);

        this.resetErrorStates();
    }

    showLoginForm(): void {
        this.currentState.set(CheckoutAccountState.loginForm);

        this.resetErrorStates();
    }

    showCreateAccountForm(): void {
        this.currentState.set(CheckoutAccountState.createAccountForm);

        this.resetErrorStates();
    }

    backToInitial(): void {
        this.currentState.set(CheckoutAccountState.initial);

        this.resetErrorStates();
    }

    submitCreateAccountForm(): void {
        const form = this.form;

        if (form && !form?.valid) {
            this.form?.form.markAllAsTouched();

            form['submitAttempt'] = true;
            form.ngSubmit.emit();

            this.detectChanges();
            return;
        }

        this.register(
            () => this.onRegisterSuccess(),
            error => this.onRegisterError(error)
        );
    }

    // Success/Error handlers
    override onLoginSuccess = () => {
        this.checkoutFormComponent.goToNextStep();
    };

    protected override getRegisterData(): Login.IRegisterFormData {
        const data = super.getRegisterData();

        data.firstname = this.shippingAddressData.firstname || '';
        data.lastname = this.shippingAddressData.lastname || '';

        return data;
    }

    private onRegisterSuccess(): void {
        this.checkoutAccountService.showAccountCreationSuccess.set(true);

        this.checkoutFormComponent.goToNextStep();
    }

    private onRegisterError(error?: any): void {
        // Check if error indicates account already exists
        if (error?.error?.includes('already exists') || error?.code === 'ACCOUNT_EXISTS') {
            this.checkoutAccountService.showAccountExistsError.set(true);

            this.detectChanges();
        }
    }

    // Get ordered blocks from content
    get orderedBlocks() {
        return (
            this.content()?.blocks?.filter(block => block.enabled) ||
            ([
                { type: 'guest_checkout', enabled: true },
                { type: 'sign_in', enabled: true }
            ] as NCheckoutAccountNewCms.IBlock[])
        );
    }

    // Check if a block is enabled
    isBlockEnabled(blockType: string): boolean {
        return this.orderedBlocks.some(block => block.type === blockType);
    }

    // Get block order
    getBlockOrder(blockType: string): number {
        return this.orderedBlocks.findIndex(block => block.type === blockType);
    }

    getBlock(type: 'guest_checkout' | 'sign_in'): NCheckoutAccountNewCms.IBlock | undefined {
        return this.orderedBlocks.find(block => block.type === type);
    }

    // Override methods from parent class
    override canUseGuestCheckout(): boolean {
        return true;
    }

    override shouldRegister(): boolean {
        return false; // We handle this differently in the new flow
    }

    override shouldLogin(): boolean {
        return false; // We handle this differently in the new flow
    }

    private resetErrorStates(): void {
        this.checkoutAccountService.showAccountExistsError.set(false);
        this.checkoutAccountService.showAccountCreationSuccess.set(false);
        this.emailBlur = false;

        if (this.form) {
            // reset form error states
            this.form?.form.markAsUntouched();
            this.form?.form.markAsPristine();

            this.form['submitAttempt'] = false;

            this.form.ngSubmit.emit();
        }

        this.detectChanges();
    }
}
